/** Assessment → parcel_id; sales → TransactionID. */
export const idKey = (reportType) =>
  String(reportType || '').toLowerCase() === 'assessment' ? 'parcel_id' : 'TransactionID';

/** DataGrid row id: parcel, or parcel-transaction when a transaction id exists. */
export const compRowId = (parcelId, transactionId) => {
  if (parcelId == null || parcelId === '') return '';
  if (transactionId == null || transactionId === '') return String(parcelId);
  return `${parcelId}-${transactionId}`;
};

/** Coerce float-string IDs ("137899.0") to ints in place on the sheet column. */
export const coerceTransactionIds = (sheet) => {
  if (!sheet?.TransactionID?.length) return sheet;
  sheet.TransactionID = sheet.TransactionID.map((id) => {
    if (id == null || id === '') return id;
    const n = Number(id);
    return Number.isFinite(n) ? Math.trunc(n) : id;
  });
  return sheet;
};

const idsFor = (sheet, reportType) => {
  if (!sheet) return null;
  const key = idKey(reportType);
  if (key === 'TransactionID') coerceTransactionIds(sheet);
  const primary = sheet[key];
  if (primary?.length) return primary;
  return key === 'TransactionID' ? null : (sheet.parcel_id || null);
};

/** number | "123.0" | "123" → comparable string */
export const normalizeCompId = (id) => {
  if (id == null || id === '') return '';
  const n = Number(id);
  return Number.isFinite(n) ? String(Math.trunc(n)) : String(id).trim();
};

/** Prefer TransactionID for sales selection identity; parcel_id otherwise. */
export const selectionKeyForRow = (row, isSaleRun = false) => {
  if (!row) return '';
  if (isSaleRun && row.TransactionID != null && row.TransactionID !== '') {
    return normalizeCompId(row.TransactionID);
  }
  return normalizeCompId(row.parcel_id ?? row.id);
};

export const selectionSetHasRow = (selectionIds, row, isSaleRun = false) => {
  if (!selectionIds?.length || !row) return false;
  const set = new Set(selectionIds.map(normalizeCompId).filter(Boolean));
  return set.has(selectionKeyForRow(row, isSaleRun));
};

/** Comp IDs already on the sheet (subject at index 0 excluded). */
export const getExistingCompIds = (sheet, reportType) =>
  (idsFor(sheet, reportType) || []).slice(1).filter((id) => id != null && id !== '');

export const findCompIndex = (sheet, id, reportType) => {
  if (id == null || id === '') return -1;
  const ids = idsFor(sheet, reportType);
  if (!ids?.length) return -1;
  const direct = ids.indexOf(id);
  if (direct >= 0) return direct;
  const needle = normalizeCompId(id);
  return needle ? ids.findIndex((x) => normalizeCompId(x) === needle) : -1;
};

export const getCompIdAtIndex = (sheet, i, reportType) =>
  sheet?.[idKey(reportType)]?.[i] ?? sheet?.parcel_id?.[i] ?? null;

/** Sales: TransactionID → parcel_id via shared sheet index. Assessment: id is already parcel_id. */
export const parcelIdForSavedCompId = (sheet, savedId, reportType) => {
  if (savedId == null || savedId === '') return null;
  if (idKey(reportType) === 'parcel_id') return savedId;
  const idx = findCompIndex(sheet, savedId, reportType);
  return idx >= 0 ? (sheet?.parcel_id?.[idx] ?? null) : null;
};

/** Persist identity ↔ parcel_ids for map/geo only. Assessment: identity is already parcel_id. */
export const mapCompIds = (sheet, ids, reportType, toParcelIds = false) => {
  if (!ids?.length) return [];
  if (idKey(reportType) === 'parcel_id' || !sheet) {
    return ids.filter((id) => id != null && id !== '');
  }
  return ids
    .map((id) => {
      if (id == null || id === '') return null;
      if (toParcelIds) {
        const idx = findCompIndex(sheet, id, reportType);
        return idx < 0 ? id : (sheet.parcel_id?.[idx] ?? id);
      }
      const idx = findCompIndex(sheet, id, 'assessment');
      return idx < 0 ? id : (getCompIdAtIndex(sheet, idx, reportType) ?? id);
    })
    .filter((id) => id != null && id !== '');
};
